// Dependencies
import { nanoid } from "nanoid/non-secure";
import React, { useCallback, useMemo } from "react";
import { components } from "react-select";
import Select from "react-select/async";

import { faAngleDown, faTimesCircle } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

//components
import DeletableChip from "../../Chip/templates/DeletableChip";
import Spacer from "../../Spacer";
import Text from "../../Text";
import { Result } from "../SelectInput";
//config
import Config from "../SelectInput/Config";
//css
import css from "./AsyncSelectInput.module.scss";

export interface AsyncSelectInputProps {
	/**
	 * The label to display above the input. This label will have a `for`
	 * attribute to specify which input it is bound to.
	 */
	label?: string;
	/**
	 * The id of the input. If not provided, a random id will be generated
	 * by nanoid.
	 * @default undefined
	 */
	inputId?: string;
	/**
	 * Whether or not the input is disabled.
	 * @default false
	 */
	disabled?: boolean;
	/**
	 * The placeholder text to display when there is no value.
	 * @default null
	 */
	placeholder?: string;
	/**
	 * The value of the input. For single select, this is a string. For
	 * multi select, this is an array of strings.
	 */
	value?: Result | Result[];

	/**
	 * Whether or not there are multiple options that can be selected.
	 * @default false
	 */
	isMultiSelect?: boolean;
	/**
	 * Whether or not the input is clearable. Renders a clear button when true.
	 * @default false
	 */
	isClearable?: boolean;
	openMenuOnFocus?: boolean;
	/**
	 * used to fetch the options for the select input
	 * @param inputValue
	 * @returns
	 */
	fetchOptions?: (inputValue: string) => Promise<Result[]>;
	/**
	 * The function to call when the input value changes.
	 */
	onChange?: (value: Result | Result[]) => void;
	/**
	 * The size of the input.
	 */
	size?: "small" | "standard";
	/**
	 * Whether or not the input is searchable.
	 * @default true
	 */
	isSearchable?: boolean;
	/**
	 * A function to format the option label. This is used to render the
	 */
	formatOptionLabel?: (option: any) => React.ReactNode;

	/**
	 * The max height of the menu
	 */
	maxMenuHeight?: number;

	/**
	 * Whether the input is in an error state
	 * @default false
	 */
	errored?: boolean;
}

/**
 * We use font awesome to render the dropdown indicator
 */
const DropdownIndicator = (props: any) => {
	return (
		<components.DropdownIndicator {...props}>
			<FontAwesomeIcon icon={faAngleDown} />
		</components.DropdownIndicator>
	);
};

/**
 * We use font awesome to render the clear button
 */
const ClearIndicator = (props: any) => {
	return (
		<components.ClearIndicator {...props}>
			<FontAwesomeIcon icon={faTimesCircle} />
		</components.ClearIndicator>
	);
};

const Menu = (props: any) => {
	return <components.Menu {...props}>{props.children}</components.Menu>;
};

/**
 * Overrides the default ValueContainer component to add a clear button and
 * an icon to the left of the input. The default clear function in react-select
 * does not work the way we want it to, so we override it here. In react-select,
 * the clear button only renders if there is a value, we want it to render if
 * there is a value or if there is text in the input.
 */
const createValueContainer = (createProps: Pick<AsyncSelectInputProps, "isClearable">) => {
	const ValueContainer = (props: any) => {
		const handleClearValue = () => {
			props.clearValue();
		};

		const isClearable = createProps.isClearable && (props.selectProps.inputValue.length > 0 || props.hasValue);

		return (
			<components.ValueContainer {...props}>
				{props.children}
				{isClearable ? (
					<div
						className={css.clearText}
						onClick={handleClearValue}
						onMouseDown={(e) => {
							e.stopPropagation();
						}}
					>
						<FontAwesomeIcon icon={faTimesCircle} />
					</div>
				) : null}
			</components.ValueContainer>
		);
	};

	return ValueContainer;
};

/**
 * @deprecated Use the Select/Combobox components from the py-components package instead
 * This component is a wrapper around the react-select component.
 * it is is very similar to the Employee Picker, but with some SelectInput component changes
 *
 * currently it is missing a lot of the typical SelectInput funcitonality (i.e. multiselect, groups, etc.)
 * we should ad that back in as needed form both the EmployeePicker and the SelectInputComponent
 */
const AsyncSelectInput = (props: AsyncSelectInputProps) => {
	const {
		label,
		value,
		onChange,
		isClearable = false,
		disabled = false,
		placeholder = "Make a selection",
		openMenuOnFocus = false,
		fetchOptions,
		size = "standard",
		isSearchable = true,
		isMultiSelect = false,
		formatOptionLabel,
		maxMenuHeight,
		errored = false,
	} = props;

	// We use the nanoid library to generate a unique id for the input. This is
	// used for the label and the input itself to ensure that they are linked
	// correctly.
	const inputId = useMemo(() => {
		return props.inputId || nanoid();
	}, [props.inputId]);

	// Some props can change the styles of the component. We use the useMemo
	// hook to memoize the styles so that we don't have to recompute them on
	// every render.
	const styles = useMemo(() => {
		return Config.customStyles({ size, showWarning: errored });
	}, [size, errored]);

	// In addition to props changing some styles, they can also change the
	// behavior of the component. We override the default ValueContainer
	// component to add the clear button and the left icon.
	const ValueContainer = useCallback(
		createValueContainer({
			isClearable,
		}),
		[isClearable]
	);

	// delete function for multivalue options
	const handleDelete = (valToDelete?: string | number) => {
		const multiValues = (value as Result[]) || [];
		if (valToDelete) {
			const filteredValues = multiValues.filter((val) => val.value !== valToDelete);
			if (onChange && filteredValues) {
				onChange(filteredValues);
			}
		}
	};

	const selectValue = value;
	const multiValues = (selectValue as Result[]) || [];

	const selectComponents = {
		Menu,
		ValueContainer,
		ClearIndicator,
		DropdownIndicator,
	};
	return (
		<div data-testid="async-select-input">
			{label ? (
				<>
					<Text element="label" size="paragraph-2" weight="semi-bold" htmlFor={inputId}>
						{label}
					</Text>
					<Spacer height={1} />
				</>
			) : null}
			<Select
				inputId={inputId}
				placeholder={placeholder === null ? "Select..." : placeholder}
				className={css.select}
				cacheOptions
				defaultOptions
				styles={styles}
				isDisabled={disabled}
				loadOptions={fetchOptions}
				components={selectComponents}
				onChange={onChange as any}
				value={value}
				openMenuOnFocus={openMenuOnFocus}
				noOptionsMessage={() => null}
				isSearchable={isSearchable}
				isMulti={isMultiSelect}
				controlShouldRenderValue={!isMultiSelect}
				formatOptionLabel={formatOptionLabel}
				maxMenuHeight={maxMenuHeight}
				isClearable={false}
			/>
			<div className={css.multiSelectOptions}>
				{isMultiSelect
					? multiValues.map((value) => (
							<DeletableChip
								className={`${css.multiValueOption} max-w-full`}
								key={value.value}
								label={value.label}
								onDelete={() => handleDelete(value.value)}
							/>
						))
					: null}
			</div>
		</div>
	);
};

/** @deprecated Use the Select/Combobox components from the py-components package instead */
export default AsyncSelectInput;
